import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { useToast } from '../hooks/use-toast';
import DesignPreview from '../components/shared/DesignPreview';
import {
  Bookmark, ExternalLink, Trash2, Search, Grid3X3, List,
  ImageIcon, FileText, Clock, Star, X, Copy,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ─── Design card (grid) ──────────────────────────────────────────────────── */
const DesignCard = ({ design, onOpen, onDuplicate, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-white border border-gray-100 transition-all duration-200"
      style={{
        boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-2px)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail — same pipeline as the Templates page (full page, no crop) */}
      <DesignPreview
        design={design}
        placeholder={
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe, #f5f3ff)' }}>
            <ImageIcon className="w-10 h-10 text-violet-300" />
          </div>
        }
      >
        {hovered && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center gap-2"
            style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(2px)' }}
          >
            <button
              onClick={() => onOpen(design)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold hover:scale-105 transition-transform"
              style={{ background: 'rgba(124,58,237,0.85)' }}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </button>
            <button
              onClick={() => onDuplicate(design)}
              className="h-8 w-8 rounded-lg text-white flex items-center justify-center hover:scale-105 transition-transform"
              style={{ background: 'rgba(255,255,255,0.25)' }}
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onRemove(design.id)}
              className="h-8 w-8 rounded-lg text-white flex items-center justify-center hover:scale-105 transition-transform"
              style={{ background: 'rgba(220,38,38,0.75)' }}
              title="Remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Bookmark badge */}
        <div className="absolute top-2 right-2 z-10">
          <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
            <Bookmark className="w-3 h-3 text-white fill-white" />
          </div>
        </div>
      </DesignPreview>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-semibold text-gray-800 truncate">{design.name || 'Untitled Design'}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-gray-400">{design.category || 'Design'}</span>
          <span className="text-[11px] text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timeAgo(design.savedAt || design.updatedAt || new Date().toISOString())}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Template / Design row (list) ───────────────────────────────────────── */
const ItemRow = ({ item, onOpen, onDuplicate, onRemove, isTemplate }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="flex items-center gap-4 p-3 rounded-xl border transition-all duration-150 bg-white"
      style={{
        background: hovered ? '#faf5ff' : '#fff',
        borderColor: hovered ? '#ede9fe' : '#f1f5f9',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative w-14 rounded-lg flex-shrink-0 overflow-hidden">
        <DesignPreview
          design={item}
          placeholder={
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${isTemplate ? '#e0e7ff, #f0fdf4' : '#ede9fe, #fce7f3'})` }}
            >
              {isTemplate
                ? <FileText className="w-5 h-5 text-indigo-300" />
                : <ImageIcon className="w-5 h-5 text-violet-300" />}
            </div>
          }
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{item.title || item.name || 'Untitled'}</p>
        <p className="text-xs text-gray-400 truncate">{item.category || (isTemplate ? 'Template' : 'Design')}</p>
      </div>

      <div
        className="flex items-center gap-2 flex-shrink-0 transition-opacity"
        style={{ opacity: hovered ? 1 : 0 }}
      >
        <button
          onClick={() => onOpen(item)}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg text-violet-600 hover:bg-violet-100 transition-colors"
        >
          {isTemplate ? 'Use' : 'Open'}
        </button>
        <button
          onClick={() => onDuplicate(item)}
          className="h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-100 flex items-center justify-center transition-colors"
          title="Duplicate"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onRemove(item.id)}
          className="h-8 w-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* ─── Empty state ─────────────────────────────────────────────────────────── */
const EmptyState = ({ icon: Icon, title, subtitle, action, onAction }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-violet-300" />
    </div>
    <p className="text-base font-semibold text-gray-700 mb-1">{title}</p>
    <p className="text-sm text-gray-400 mb-5 max-w-xs">{subtitle}</p>
    {action && (
      <button
        onClick={onAction}
        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}
      >
        {action}
      </button>
    )}
  </div>
);

/* ─── Skeleton ─────────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden bg-white border border-gray-100">
    <div className="h-40 bg-gray-100 animate-pulse" />
    <div className="p-3 space-y-2">
      <div className="h-3.5 bg-gray-100 rounded animate-pulse w-3/4" />
      <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
    </div>
  </div>
);

/* ─── Tabs ─────────────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'designs',   label: 'Saved Designs',        icon: ImageIcon },
  { id: 'templates', label: 'Bookmarked Templates',  icon: Star },
];

/* ═══════════════════════════════════════════════════════════════
   UserSaved
═══════════════════════════════════════════════════════════════ */
const UserSaved = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tab, setTab] = useState('designs');
  const [loading, setLoading] = useState(true);
  const [designs, setDesigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  /* Auth guard */
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);
    if (!token || !user) { navigate('/'); return; }
    fetchSaved(token);
  }, []);

  const fetchSaved = async (token) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dRes, tRes] = await Promise.allSettled([
        API_URL ? fetch(`${API_URL}/api/designs`, { headers }) : Promise.resolve(null),
        API_URL ? fetch(`${API_URL}/api/user/saved-templates`, { headers }) : Promise.resolve(null),
      ]);
      if (dRes.status === 'fulfilled' && dRes.value?.ok) {
        const data = await dRes.value.json();
        setDesigns(Array.isArray(data) ? data : data.designs || []);
      }
      if (tRes.status === 'fulfilled' && tRes.value?.ok) {
        const data = await tRes.value.json();
        setTemplates(Array.isArray(data) ? data : data.templates || []);
      }
    } catch { /* empty state shown */ }
    finally { setLoading(false); }
  };

  /* ── Design actions ── */
  const removeDesign = async (id) => {
    setDesigns(prev => prev.filter(d => d.id !== id));
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (API_URL && token) {
      try { await fetch(`${API_URL}/api/designs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); }
      catch { /* ignore */ }
    }
    toast({ title: 'Removed', description: 'Design removed from saved.' });
  };

  const duplicateDesign = async (design) => {
    // Persist the copy (POST /api/designs) so it opens correctly and
    // survives refresh; inherits the source thumbnail immediately.
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!API_URL || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${design.name || 'Untitled'} (copy)`,
          canvas_size: design.canvas_size || { width: 1080, height: 1080 },
          pages: design.pages || [],
          current_page_id: design.current_page_id || null,
          thumbnail: design.thumbnail || null,
          thumbnail_url: design.thumbnail_url || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setDesigns(prev => [created, ...prev]);
      toast({ title: 'Duplicated', description: `"${design.name || 'Untitled'}" duplicated.` });
    } catch {
      toast({ title: 'Duplicate failed', description: 'Could not duplicate this design.', variant: 'destructive' });
    }
  };

  /* ── Template actions ── */
  const removeTemplate = async (id) => {
    setTemplates(prev => prev.filter(t => t.id !== id));
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (API_URL && token) {
      try { await fetch(`${API_URL}/api/user/saved-templates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); }
      catch { /* ignore */ }
    }
    toast({ title: 'Removed', description: 'Template removed from bookmarks.' });
  };

  const duplicateTemplate = (template) => {
    const dup = { ...template, id: `dup_${Date.now()}`, title: `${template.title || 'Untitled'} (copy)` };
    setTemplates(prev => [dup, ...prev]);
    toast({ title: 'Duplicated', description: `"${template.title || 'Untitled'}" duplicated.` });
  };

  /* Filter */
  const filteredDesigns = designs.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredTemplates = templates.filter(t =>
    (t.title || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8">

          {/* Page title */}
          <div className="mb-7">
            <div className="flex items-center gap-3 mb-1">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}
              >
                <Bookmark className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">Saved Files</h1>
            </div>
            <p className="text-sm text-gray-500 ml-12">Your bookmarked designs and templates in one place.</p>
          </div>

          {/* Tabs + controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
                  style={{
                    background: tab === t.id ? 'linear-gradient(135deg, #7c3aed, #ec4899)' : 'transparent',
                    color: tab === t.id ? '#fff' : '#6b7280',
                  }}
                >
                  <t.icon style={{ width: 14, height: 14 }} />
                  {t.label}
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{
                      background: tab === t.id ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                      color: tab === t.id ? '#fff' : '#9ca3af',
                    }}
                  >
                    {t.id === 'designs' ? designs.length : templates.length}
                  </span>
                </button>
              ))}
            </div>

            {/* Search + view toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-8 h-9 w-48 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 bg-white transition-all"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className="p-1.5 rounded transition-colors"
                  style={{ background: viewMode === 'grid' ? '#f5f3ff' : 'transparent', color: viewMode === 'grid' ? '#7c3aed' : '#9ca3af' }}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className="p-1.5 rounded transition-colors"
                  style={{ background: viewMode === 'list' ? '#f5f3ff' : 'transparent', color: viewMode === 'list' ? '#7c3aed' : '#9ca3af' }}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl border border-gray-100 min-h-[400px]" style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
            {loading ? (
              <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : tab === 'designs' ? (
              filteredDesigns.length === 0 ? (
                <EmptyState
                  icon={Bookmark}
                  title={search ? 'No results found' : 'No saved designs yet'}
                  subtitle={search ? `No designs match "${search}"` : 'Save designs from the editor to find them here.'}
                  action={search ? null : 'Open Editor'}
                  onAction={() => navigate('/editor')}
                />
              ) : viewMode === 'grid' ? (
                <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredDesigns.map(d => (
                    <DesignCard
                      key={d.id}
                      design={d}
                      onOpen={d => navigate(`/editor/${d.id}`)}
                      onDuplicate={duplicateDesign}
                      onRemove={removeDesign}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-4 flex flex-col gap-2">
                  {filteredDesigns.map(d => (
                    <ItemRow
                      key={d.id}
                      item={d}
                      onOpen={d => navigate(`/editor/${d.id}`)}
                      onDuplicate={duplicateDesign}
                      onRemove={removeDesign}
                      isTemplate={false}
                    />
                  ))}
                </div>
              )
            ) : (
              filteredTemplates.length === 0 ? (
                <EmptyState
                  icon={Star}
                  title={search ? 'No results found' : 'No bookmarked templates yet'}
                  subtitle={search ? `No templates match "${search}"` : 'Browse templates and bookmark the ones you love.'}
                  action={search ? null : 'Browse Templates'}
                  onAction={() => navigate('/templates')}
                />
              ) : (
                <div className="p-4 flex flex-col gap-2">
                  {filteredTemplates.map(t => (
                    <ItemRow
                      key={t.id}
                      item={t}
                      onOpen={t => navigate(`/editor?template=${t.id}`)}
                      onDuplicate={duplicateTemplate}
                      onRemove={removeTemplate}
                      isTemplate
                    />
                  ))}
                </div>
              )
            )}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UserSaved;
